import {Person} from "../../../services/interface/query/person";
import {useTranslation} from "react-i18next";
import React, {useEffect, useState} from "react";
import NotPermissionComponent from "../NotPermissionComponent";
import {useFindChecklistProvidences} from "../../../services/apis/apiChecklist";
import {ChecklistProvidenceDetailInterface} from "../../../services/interface/query/checklist-interface";
import NoRecords from "../../components/NoRecords";
import * as IconsMui from "@mui/icons-material";
import {IconButton, Collapse} from "@material-tailwind/react";
import CustomTable from "../../../components/general/CustomTable";

interface Props {
    person?: Person;
}

/**
 * Muestra providencias judiciales asociadas a la persona consultada.
 * Consume el endpoint POST /control_lists/providences.
 *
 * @author cfernandez
 * @version 1.0.0
 * @since 2026-04-08
 */
export default function ChecklistProvidences({person}: Props) {
    const { t } = useTranslation("global") as { t: (key: string) => string };

    const {
        mutate: findProvidences,
        data: successProvidences,
        error: errorProvidences,
        isPending: loadingProvidences,
    } = useFindChecklistProvidences()

    const [response, setResponse] = useState<ChecklistProvidenceDetailInterface[] | null | undefined>()
    const [isOpen, setIsOpen] = React.useState(false);

    useEffect(() => {
        if (person?.identification) {
            setResponse(undefined)
            findProvidences({identifier: person.identification, msj: t('providences')})
        }
    }, [person]);

    useEffect(() => {
        if (successProvidences !== undefined) {
            setResponse(successProvidences);
        }
    }, [successProvidences]);

    const isResolved = response !== undefined && response !== null;
    const hasRecords = isResolved && response.length > 0;

    return (
        <NotPermissionComponent
            className={'min-h-[50px]'}
            title={'/pn_checklist'}
            loading={loadingProvidences}
            error={errorProvidences}
            component={
                <div>
                    <div className={'flex flex-row gap-3 !border border-transparent border-b-secondary pb-3'}>
                        {isResolved ?
                            !hasRecords ?
                                <IconsMui.EmojiEmotionsTwoTone className={'text-success'}/> :
                                <IconsMui.SentimentDissatisfiedTwoTone className={'text-danger'}/> :
                            <IconsMui.NotificationsOffTwoTone className={'text-empColorSecondary'}/>
                        }
                        {t('providences')}
                        <div className={'ml-auto flex flex-row gap-3 items-center'}>
                            {hasRecords &&
                                <div className={'text-[9px] font-light text-danger'}>{t('providences')}</div>}
                            {hasRecords &&
                                <IconButton placeholder="icon-button"
                                            size="sm"
                                            variant="text"
                                            onClick={() => setIsOpen((cur) => !cur)}
                                >
                                    <IconsMui.KeyboardArrowDownTwoTone className="h-6 w-6"/>
                                </IconButton>
                            }
                        </div>
                    </div>
                    {hasRecords ?
                        <Collapse open={isOpen} className="bg-white">
                            <CustomTable
                                classNameTable={'mt-2'}
                                header={[
                                    t('cl-sender'),
                                    t('cl-issuing-entity'),
                                    t('cl-trial-type'),
                                    t('cl-trial-number'),
                                    t('date'),
                                    t('observations'),
                                ]}
                                listElement={response?.map((item) => ({
                                    row: [
                                        item.sender,
                                        item.issuingEntity,
                                        item.trialType,
                                        item.trialNumber,
                                        item.date,
                                        item.observations,
                                    ]
                                }))}
                            />
                        </Collapse>
                        : !loadingProvidences && isResolved && <NoRecords/>
                    }
                </div>
            }
        />
    )
}
